import React, { FC, Fragment } from 'react';
import { TableCell, TableRow, Button, Chip } from '@mui/material';
import ActiveIndicator from 'components/users/shared/active-indicator/active-indicator.component';
import { useTranslation } from 'react-i18next';
import { LinkedOrganization, User } from 'models/user';
import { Organization } from 'models/organization';
import { resolveUserOrganization } from 'utils/user-organization';

type TechniciansItemProps = {
    technician: User;
    onEditTechnicianClick: (technician: User) => void;
    onLoginClick?: (user: User) => void;
};

const TechniciansItem: FC<TechniciansItemProps> = ({
    technician,
    onEditTechnicianClick,
    onLoginClick,
}: TechniciansItemProps) => {
    const { t: translate } = useTranslation();
    // Derived, not state: the list is keyed by index, so a cached value outlived its
    // row and showed the previous user's role and lab on a filter change.
    const userOrganization = resolveUserOrganization(technician);

    const handleLoginClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.stopPropagation();
        if (onLoginClick) {
            onLoginClick(technician);
        }
    };

    return (
        <TableRow
            hover
            sx={{ cursor: 'pointer' }}
            onClick={() => onEditTechnicianClick(Object.assign({}, technician, userOrganization))}
        >
            <Fragment>
                <TableCell sx={{ width: 10 }} align="center">
                    <ActiveIndicator isActive={!!userOrganization?.active} />
                </TableCell>
                <TableCell sx={{ width: 300 }}>
                    {technician.fullName}
                    {technician.isShared && (
                        <Chip
                            label={translate('common:SHARED_USER')}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ ml: 1 }}
                        />
                    )}
                </TableCell>
                <TableCell>{technician.email}</TableCell>
                <TableCell>{translate(userOrganization.role)}</TableCell>
                {technician.linkedOrganizations?.length > 1 ? (
                    <TableCell>
                        {technician.linkedOrganizations
                            .map(
                                (linkedOrganization: LinkedOrganization) =>
                                    (linkedOrganization.organization as Organization).name,
                            )
                            .join(', ')}
                    </TableCell>
                ) : (
                    <TableCell>{(userOrganization.organization as Organization)?.name}</TableCell>
                )}
                <TableCell align="center">
                    <Button onClick={handleLoginClick}>{translate('users:LOGIN')}</Button>
                </TableCell>
            </Fragment>
        </TableRow>
    );
};

export default TechniciansItem;
